<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Policy from 'dashboard/components/policy.vue';
import DealForm from 'dashboard/components-next/Deals/DealForm/DealForm.vue';
import ConfirmDealDeleteDialog from 'dashboard/components-next/Deals/DealCard/ConfirmDealDeleteDialog.vue';
import Timeline from 'dashboard/components-next/Timeline/Timeline.vue';

const props = defineProps({
  deal: { type: Object, required: true },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  isDeleting: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  disableExpand: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle',
  'updateDeal',
  'deleteDeal',
  'select',
  'nameClick',
]);

const { t } = useI18n();

const getAgentById = useMapGetter('agents/getAgentById');

const dealFormRef = ref(null);
const confirmDeleteDialogRef = ref(null);
const isHistoryPanelOpen = ref(false);

const isFormInvalid = computed(() => dealFormRef.value?.isFormInvalid);

const displayName = computed(
  () => props.deal.name || t('DEALS.EMPTY_STATE.TITLE')
);

const stage = computed(() => props.deal.pipelineStage || null);
const pipelineName = computed(() => props.deal.pipeline?.name || '');
const companyName = computed(() => {
  const names = (props.deal.companies || []).map(c => c.name);
  return names.length ? names.join(', ') : props.deal.company?.name || '';
});

const assignee = computed(() => {
  if (!props.deal.assignedToId) return null;
  const agent = getAgentById.value(props.deal.assignedToId);
  return agent?.id ? agent : (props.deal.assignedTo ?? null);
});

const assigneeLabel = computed(() => {
  if (!assignee.value) return '—';
  const name = assignee.value.name || assignee.value.email;
  return assignee.value.active === false
    ? `${name} ${t('DEALS.DEACTIVATED_AGENT_SUFFIX')}`
    : name;
});

const formattedValue = computed(() => {
  if (!props.deal.value) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.deal.currency || 'BRL',
  }).format(props.deal.value);
});

const onClickExpand = () => emit('toggle');

const toggleSelect = checked => emit('select', checked);

const handleSave = async () => {
  const updated = await dealFormRef.value?.save();
  if (updated) emit('updateDeal', updated);
};

const openConfirmDeleteDialog = () => {
  confirmDeleteDialogRef.value?.dialogRef.open();
};

const handleConfirmDelete = () => {
  emit('deleteDeal', props.deal.id);
};
</script>

<template>
  <div class="relative">
    <CardLayout
      layout="row"
      :class="{
        'outline-n-weak !bg-n-slate-3 dark:!bg-n-solid-3': isSelected,
      }"
    >
      <div class="flex items-center flex-1 min-w-0 gap-4" @click.stop>
        <Checkbox
          v-if="selectable"
          :model-value="isSelected"
          class="shrink-0"
          @change="event => toggleSelect(event.target.checked)"
          @click.stop
        />
        <div class="flex flex-col gap-0.5 flex-1 min-w-0">
          <span
            class="text-sm font-medium truncate text-n-slate-12"
            :class="{ 'cursor-pointer hover:underline': disableExpand }"
            @click="disableExpand && emit('nameClick')"
          >
            {{ displayName }}
          </span>
          <div v-if="deal.labels?.length" class="flex flex-wrap gap-1 mt-1">
            <span
              v-for="label in deal.labels"
              :key="label.title"
              class="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full border border-n-weak text-n-slate-11"
            >
              <span
                class="size-1.5 rounded-full shrink-0"
                :style="{ backgroundColor: label.color }"
              />
              {{ label.title }}
            </span>
          </div>
        </div>

        <span class="flex-1 min-w-0 text-sm truncate text-n-slate-11">
          {{ companyName || '—' }}
        </span>

        <div class="flex-1 min-w-0">
          <span
            v-if="stage"
            class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium text-white max-w-full"
            :style="{ backgroundColor: stage.color || '#6366f1' }"
            :title="
              pipelineName ? `${pipelineName} · ${stage.name}` : stage.name
            "
          >
            <span class="truncate">{{ stage.name }}</span>
          </span>
          <span v-else class="text-sm text-n-slate-10">—</span>
        </div>

        <div class="flex items-center flex-1 min-w-0 gap-2">
          <Avatar
            v-if="assignee"
            :name="assignee.name || assignee.email"
            :src="assignee.thumbnail"
            :size="24"
            rounded-full
            class="shrink-0"
          />
          <span class="text-sm truncate text-n-slate-11">
            {{ assigneeLabel }}
          </span>
        </div>

        <span
          class="w-28 text-sm font-semibold text-right shrink-0 text-green-700"
        >
          {{ formattedValue || '—' }}
        </span>
      </div>

      <div
        v-if="!disableExpand"
        class="flex flex-col items-center gap-1 shrink-0"
      >
        <Button
          icon="i-lucide-chevron-down"
          variant="ghost"
          color="slate"
          size="xs"
          :class="{ 'rotate-180': isExpanded }"
          @click="onClickExpand"
        />
        <Button
          v-if="isExpanded"
          icon="i-lucide-history"
          variant="ghost"
          :color="isHistoryPanelOpen ? 'blue' : 'slate'"
          size="xs"
          @click="isHistoryPanelOpen = !isHistoryPanelOpen"
        />
      </div>

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div class="flex items-start gap-6 p-6 border-t border-n-strong">
              <div
                class="flex flex-col gap-6 min-w-0"
                :class="isHistoryPanelOpen ? 'w-1/2' : 'flex-1'"
              >
                <DealForm ref="dealFormRef" :deal-data="deal" />
                <div>
                  <Button
                    :label="t('DEALS.LIST.SAVE')"
                    size="sm"
                    :is-loading="isUpdating"
                    :disabled="isUpdating || isFormInvalid"
                    @click="handleSave"
                  />
                </div>
              </div>

              <div
                v-if="isHistoryPanelOpen"
                class="flex flex-col gap-3 w-1/2 min-w-0 border-l border-n-strong pl-6"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('TIMELINE.TAB_LABEL') }}
                </span>
                <Timeline subject-type="Deal" :subject-id="deal.id" />
              </div>
            </div>

            <Policy :permissions="['administrator']">
              <div
                class="flex flex-col items-start border-t border-n-strong px-6 py-5"
              >
                <span
                  class="inline-flex text-n-slate-11 text-sm items-center gap-1"
                >
                  {{ t('DEALS.LIST.DELETE.SECTION_DESCRIPTION') }}
                  <Button
                    :label="t('DEALS.LIST.DELETE.CONFIRM')"
                    sm
                    ruby
                    link
                    @click="openConfirmDeleteDialog"
                  />
                </span>
              </div>

              <ConfirmDealDeleteDialog
                ref="confirmDeleteDialogRef"
                :deal="{ id: deal.id, name: deal.name }"
                :is-loading="isDeleting"
                @confirm="handleConfirmDelete"
              />
            </Policy>
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
